import { useQuery } from "@tanstack/react-query";
import { pilot } from "@/lib/api";
import { fmtDate, fmtHours } from "@/lib/format";

/** Seconds as "7h 33m" / "42m". Duty and turnarounds are read in hours and
 *  minutes, not decimal hours — fmtHours() is for logged block time. */
function fmtHM(sec) {
  if (sec == null) return "—";
  const m = Math.round(sec / 60);
  const h = Math.floor(m / 60);
  return h ? `${h}h ${String(m % 60).padStart(2, "0")}m` : `${m}m`;
}

function LdgStat({ label, value, last }) {
  return (
    <div className={`px-6 py-4 bg-card ${last ? "" : "border-r border-border"} border-b md:border-b-0`}>
      <div className="overline mb-1">{label}</div>
      <div className="text-xl font-semibold telemetry-value">{value}</div>
    </div>
  );
}

export default function Logbook() {
  const { data = [], isLoading } = useQuery({ queryKey: ["logbook"], queryFn: () => pilot.logbook(200) });

  // Landing rates measured against this pilot's own history. The column has
  // been filling for years and nobody has ever read it back to them.
  const { data: ldg } = useQuery({
    queryKey: ["landing-stats"],
    queryFn: pilot.landingStats,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // The shape of the working day: how long on the ground between sectors, and
  // how long from first push to last chocks.
  const { data: duty } = useQuery({
    queryKey: ["duty", 30],
    queryFn: () => pilot.duty(30),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  return (
    <div className="p-8 max-w-[1400px]" data-testid="logbook-page">
      <div className="mb-6">
        <div className="overline mb-1">Career</div>
        <h1 className="text-3xl font-semibold tracking-tight">Logbook</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Every PIREP submitted through FlyVAC and vaCARS, straight from your FlyVAC logbook table.
        </p>
      </div>

      {ldg?.measured > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-border rounded-lg overflow-hidden mb-6"
             data-testid="logbook-landing-stats">
          <LdgStat label="Landings measured" value={ldg.measured} />
          <LdgStat label="Smoothest"  value={`${Math.round(ldg.best)} fpm`} />
          <LdgStat label="Typical"    value={`${Math.round(ldg.median)} fpm`} />
          <LdgStat label="Firmest"    value={`${Math.round(ldg.worst)} fpm`} last />
        </div>
      )}

      {duty?.days?.length > 0 && (
        <section className="card-flat mb-6 overflow-hidden" data-testid="logbook-duty">
          <div className="px-6 py-4 border-b border-border">
            <div className="overline">Last 30 days</div>
            <h2 className="text-lg font-semibold">Duty</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Duty is first push to last chocks, so ground time between sectors
              counts. A gap over six hours is a night stop, not a turnaround.
            </p>
          </div>
          <div className="divide-y divide-border">
            {duty.days.slice(0, 7).map((d) => (
              <div key={d.date} className="px-6 py-3 grid grid-cols-12 items-center text-sm"
                   data-testid={`duty-day-${d.date}`}>
                <div className="col-span-3 font-mono">{d.date}</div>
                <div className="col-span-2 font-mono text-muted-foreground">
                  {d.sectors} {d.sectors === 1 ? "sector" : "sectors"}
                </div>
                <div className="col-span-3 font-mono">
                  <span className="text-muted-foreground text-xs mr-2">block</span>
                  {fmtHM(d.block_sec)}
                </div>
                <div className="col-span-4 font-mono text-right">
                  <span className="text-muted-foreground text-xs mr-2">duty</span>
                  {fmtHM(d.duty_sec)}
                </div>
              </div>
            ))}
          </div>
          {duty.sectors?.some((x) => x.turnaround_sec != null || x.night_stop) && (
            <div className="px-6 py-3 border-t border-border flex flex-wrap gap-x-5 gap-y-1 text-xs font-mono">
              <span className="text-muted-foreground">Recent turns:</span>
              {duty.sectors.filter((x) => x.turnaround_sec != null || x.night_stop)
                .slice(0, 5).map((x) => (
                  <span key={x.id}>
                    <span className="text-muted-foreground">{x.orig}</span>{" "}
                    {x.night_stop
                      ? <span className="text-muted-foreground/70">night stop</span>
                      : <span className={x.turnaround_sec < 1800 ? "text-primary" : ""}>
                          {fmtHM(x.turnaround_sec)}
                        </span>}
                  </span>
                ))}
            </div>
          )}
        </section>
      )}

      <div className="card-flat overflow-hidden">
        <div className="grid grid-cols-12 px-6 py-2 overline border-b border-border bg-secondary/40">
          <div className="col-span-1">#</div>
          <div className="col-span-1">Flight</div>
          <div className="col-span-2">Route</div>
          <div className="col-span-1">Type</div>
          <div className="col-span-1">Reg</div>
          <div className="col-span-1">Pax</div>
          <div className="col-span-2">Date</div>
          <div className="col-span-2">Block time</div>
          <div className="col-span-1 text-right">Ldg rate</div>
        </div>
        {isLoading ? (
          <div className="p-8 text-sm text-muted-foreground">Loading…</div>
        ) : data.length === 0 ? (
          <div className="p-8 text-sm text-muted-foreground">
            No PIREPs yet. Fly your first bid and it will show up here.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {data.map((p) => {
              const block = (p.time_in && p.time_out) ? Number(p.time_in) - Number(p.time_out) : 0;
              return (
                <div key={p.id} className="grid grid-cols-12 px-6 py-3 items-center data-row text-sm" data-testid={`logbook-row-${p.id}`}>
                  <div className="col-span-1 font-mono text-muted-foreground">{p.id}</div>
                  <div className="col-span-1 font-mono">{p.flt}</div>
                  <div className="col-span-2 font-mono">{p.orig} → {p.dest}</div>
                  <div className="col-span-1 font-mono text-muted-foreground">{p.type}</div>
                  <div className="col-span-1 font-mono text-muted-foreground">{p.reg || "—"}</div>
                  <div className="col-span-1 font-mono">{p.pax || 0}</div>
                  <div className="col-span-2 font-mono text-muted-foreground">{fmtDate(p.time_in)}</div>
                  <div className="col-span-2 font-mono">{fmtHours(block)}</div>
                  <div className="col-span-1 text-right telemetry-value">
                    {p.ldg_vs ? Number(p.ldg_vs).toFixed(0) : "—"}
                    {/* Percentile among this pilot's own landings. Only for
                        rows the server actually counted — a flight whose rate
                        was never measured has no rank, and inventing one would
                        reward the telemetry dropping out. */}
                    {/* "beats 75%", not "top 25%". The inverted form read as a
                        ranking where 100 was best, so the FIRMEST landing in
                        the book was labelled "top 100%" — the exact opposite
                        of what it was. This is the number the server computes,
                        stated plainly. */}
                    {ldg?.by_id?.[String(p.id)] !== undefined && (
                      <div className="text-[10px] font-mono text-muted-foreground mt-0.5"
                           title={`Smoother than ${ldg.by_id[String(p.id)]}% of your landings`}>
                        beats {ldg.by_id[String(p.id)]}%
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
